import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, FileText } from "@phosphor-icons/react";
import { useApi, fmtDate } from "../lib/api";
import { useT } from "../lib/i18n";
import { PageHeader, GlassCard, Loader, Empty, Badge, useChildren, ChildPicker, fadeUp } from "../components/kit";

export default function Results() {
  const { t } = useT();
  const { kids, sel, setSel } = useChildren();
  const { data, loading } = useApi(sel ? `/results/${sel}` : null);
  return (
    <div>
      <PageHeader eyebrow={data ? `${data.student.name} · ${data.student.class_name}` : ""} title={t("Results")} subtitle="Published exam results and report cards."
        actions={<ChildPicker kids={kids} sel={sel} setSel={setSel} />} />
      {loading || !data ? <Loader /> : !data.results.length ? <GlassCard><Empty hint="Results appear here once the school publishes them." /></GlassCard> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {data.results.map((r, i) => (
            <motion.div key={r.exam_id} {...fadeUp(i)}>
              <GlassCard className="p-6 sm:p-7 h-full" data-testid={`result-card-${r.exam_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="eyebrow">{fmtDate(r.date)}</div>
                    <h3 className="mt-2 font-display text-2xl font-semibold text-white">{r.exam_name}</h3>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-4xl font-semibold text-gradient" data-testid={`result-percent-${r.exam_id}`}>{r.percent != null ? `${r.percent}%` : "—"}</div>
                    <div className="text-xs text-zinc-400">{t("Grade")} {r.grade}{r.rank ? ` · ${t("Rank")} ${r.rank}/${r.class_size}` : ""}</div>
                  </div>
                </div>
                <div className="mt-6 space-y-3">
                  {r.subjects.map((s) => (
                    <div key={s.name}>
                      <div className="flex justify-between text-sm"><span className="text-zinc-300">{s.name}</span><span className="text-white">{s.marks ?? "—"}<span className="text-zinc-500">/{s.max_marks}</span> <span className="ml-2 text-xs text-blue-300">{s.grade}</span></span></div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${s.marks != null ? (s.marks * 100) / s.max_marks : 0}%` }} transition={{ duration: 0.9, delay: 0.2 }} className="h-full rounded-full bg-gradient-to-r from-blue-500 to-orange-400" />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <Badge status={r.passed ? "paid" : "A"}>{r.passed ? "Pass" : "Needs improvement"}</Badge>
                  <Link to={`/report-card/${sel}/${r.exam_id}`} className="btn-ghost" data-testid={`report-card-link-${r.exam_id}`}><FileText size={16} /> {t("Report card")}</Link>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      )}
      <p className="mt-6 flex items-center gap-2 text-xs text-zinc-500"><Trophy size={14} /> Grades: A+ ≥90, A ≥80, B+ ≥70, B ≥60, C ≥50, D ≥40</p>
    </div>
  );
}
